/**
 * RPDM W1-D · Team Read-Only View (structural placeholder).
 *
 * SCOPE: Renders a read-only membership table sourced from the
 * existing engagement payload. NO invite, NO remove, NO role editing,
 * NO PSA-controlled surfaces. Members roster is derived from what
 * `GET /projects/engagement/:id` already returns.
 *
 * Route: /projects/:id/team   (when master + team flags both ON)
 */
import ProjectWorkspaceShell from "./ProjectWorkspaceShell";
import WorkspaceWidgetShell from "./WorkspaceWidgetShell";
import { Badge } from "../../../components/ui/badge";

function _collectMembers(engagement) {
    // Deterministic assembly from fields today's payload already
    // exposes. If none of these are present, the widget renders empty
    // and prints a "no members recorded" caption — no invention.
    const seen = new Map();
    const push = (person, role) => {
        if (!person) return;
        const key = person.id || person.user_id || person.email || person.name;
        if (!key) return;
        if (seen.has(key)) return;
        seen.set(key, {
            id: key,
            name: person.name || person.email || key,
            email: person.email || "",
            role,
        });
    };
    push(engagement.owner || engagement.owner_user, "Owner");
    push(engagement.created_by_user, "Creator");
    push(engagement.pm || engagement.project_manager_user, "Project Manager");
    (engagement.members || []).forEach((m) =>
        push(m.user || m, m.role || "Member"),
    );
    return Array.from(seen.values());
}

export default function ProjectTeamReadOnly() {
    return (
        <ProjectWorkspaceShell>
            {(view) => {
                const members = _collectMembers(view.raw);
                return (
                    <div className="mx-auto max-w-6xl" data-testid="rpdm-team-page">
                        <WorkspaceWidgetShell
                            title="Team Members (read-only)"
                            description="Derived from the engagement payload — no mutation surface in W1-D."
                            testId="rpdm-widget-team"
                        >
                            {members.length === 0 ? (
                                <p className="text-xs text-slate-500" data-testid="rpdm-team-empty">
                                    No members recorded for this engagement. Member
                                    management arrives in a later wave.
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table
                                        className="w-full text-left text-sm"
                                        data-testid="rpdm-team-table"
                                    >
                                        <thead className="text-xs text-slate-500">
                                            <tr>
                                                <th className="py-1.5 pr-3 font-medium">Name</th>
                                                <th className="py-1.5 pr-3 font-medium">Email</th>
                                                <th className="py-1.5 pr-3 font-medium">Role</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {members.map((m) => (
                                                <tr
                                                    key={m.id}
                                                    className="border-t border-slate-100"
                                                    data-testid={`rpdm-team-row-${m.id}`}
                                                >
                                                    <td className="py-2 pr-3 text-slate-800">{m.name}</td>
                                                    <td className="py-2 pr-3 text-slate-600">{m.email || "—"}</td>
                                                    <td className="py-2 pr-3">
                                                        <Badge
                                                            variant="outline"
                                                            className="border-slate-300 bg-slate-50 text-xs text-slate-600"
                                                        >
                                                            {m.role}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </WorkspaceWidgetShell>
                    </div>
                );
            }}
        </ProjectWorkspaceShell>
    );
}
